import { useState, useEffect } from 'react';
import { ftpApi, sftpApi, mappingsApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import {
  Server, Wifi, Settings2, CheckCircle, AlertCircle, Loader,
  Key, Copy, RefreshCw, Shield, ChevronDown, ChevronUp,
  TestTube, Save, XCircle, X
, Info } from 'lucide-react';

const INTERVALS = [
  { value: 60,   label: 'Every 1 minute' },
  { value: 300,  label: 'Every 5 minutes' },
  { value: 900,  label: 'Every 15 minutes' },
  { value: 1800, label: 'Every 30 minutes' },
  { value: 3600, label: 'Every hour' },
];

// ── SFTP Panel ────────────────────────────────────────────────────────────────
function SFTPPanel() {
  const { user } = useAuth();
  const isAdmin = user?.is_admin;
  const [config, setConfig]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [provisioning, setProv]   = useState(false);
  const [privateKey, setKey]      = useState(null);
  const [showKey, setShowKey]     = useState(false);
  const [rotating, setRotating]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState({
    poll_interval: 300, schedule_enabled: true,
    alert_email: '', alert_slack_webhook: '',
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await sftpApi.getConfig();
      setConfig(res.data);
      setForm({
        poll_interval:       res.data.poll_interval || 300,
        schedule_enabled:    res.data.schedule_enabled ?? true,
        alert_email:         res.data.alert_email || '',
        alert_slack_webhook: res.data.alert_slack_webhook || '',
      });
    } catch { toast.error('Failed to load SFTP config'); }
    finally { setLoading(false); }
  };

  const handleProvision = async () => {
    if (!window.confirm('This will create an AWS Transfer Family SFTP server, S3 bucket, and SSH key pair. Continue?')) return;
    setProv(true);
    try {
      await sftpApi.provision();
      await load();
      const keyRes = await sftpApi.getPrivateKey();
      setKey(keyRes.data.private_key);
      setShowKey(true);
      toast.success('SFTP provisioned — copy the private key and give it to AP21');
    } catch (e) { toast.error(e.response?.data?.detail || 'Provisioning failed'); }
    finally { setProv(false); }
  };

  const handleShowKey = async () => {
    if (showKey) { setShowKey(false); return; }
    try {
      const res = await sftpApi.getPrivateKey();
      setKey(res.data.private_key);
      setShowKey(true);
    } catch { toast.error('Failed to retrieve private key'); }
  };

  const handleRotate = async () => {
    if (!window.confirm('This immediately invalidates the current key. AP21 cannot connect until you provide the new key. Continue?')) return;
    setRotating(true);
    try {
      await sftpApi.rotateKey();
      const keyRes = await sftpApi.getPrivateKey();
      setKey(keyRes.data.private_key);
      setShowKey(true);
      toast.success('SSH key rotated — provide the new private key to AP21');
    } catch { toast.error('Key rotation failed'); }
    finally { setRotating(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await sftpApi.updateConfig(form);
      toast.success('Settings saved');
    } catch { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  const handleDeprovision = async () => {
    const confirmed = window.confirm(
      'Deprovision SFTP?\n\nThis will permanently:\n• Revoke AP21\'s SFTP access immediately\n• Delete the S3 bucket and ALL files (incoming, outgoing, archive)\n• Remove the AWS Transfer Family user and IAM role\n\nDownload any files you need before proceeding.\n\nThis cannot be undone.'
    );
    if (!confirmed) return;
    try {
      const { data } = await sftpApi.deprovision();
      toast.success('SFTP deprovisioned — AP21 can no longer connect via SFTP');
      if (data.warnings?.length) {
        toast.error(`Deprovisioned with warnings: ${data.warnings.join(', ')}`, { duration: 8000 });
      }
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Deprovision failed'); }
  };

  const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Loader size={24} className="spin" style={{ color: 'var(--text-dim)' }} /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Connection details / provision */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: config?.provisioned ? 20 : 12 }}>
          {config?.provisioned
            ? <CheckCircle size={16} style={{ color: 'var(--green)' }} />
            : <AlertCircle size={16} style={{ color: 'var(--text-dim)' }} />}
          <span className="card-title" style={{ margin: 0 }}>
            {config?.provisioned ? 'SFTP Active' : 'Not Provisioned'}
          </span>
        </div>

        {config?.provisioned ? (
          <>
            {[
              ['SFTP Endpoint',   config.sftp_endpoint,    true],
              ['Username',        config.transfer_username, true],
              ['Port',            '22',                     false],
              ['Incoming folder', `/${config.incoming_prefix}`, false],
              ['Outgoing folder', `/${config.outgoing_prefix}`, false],
              ...(isAdmin ? [['S3 Bucket', config.bucket_name, false]] : []),
            ].map(([label, value, canCopy]) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--navy-border)' }}>
                <span style={{ fontSize: 13, color: 'var(--text-dim)', width: 150, flexShrink: 0 }}>{label}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
                  {canCopy && value && (
                    <button onClick={() => copy(value, label)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 4, flexShrink: 0 }}>
                      <Copy size={13} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--navy)', borderRadius: 8, border: '1px solid var(--navy-border)', fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--blue)' }}>Instructions for AP21 — </strong>
              Connect via SFTP using the endpoint and username above, authenticating with the SSH private key below. Upload CSV files to the <strong>incoming/</strong> folder.
            </div>
            {isAdmin && (
              <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--navy-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-dim)' }}>Deprovision SFTP</div>
                  <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 2 }}>Permanently removes all AWS resources and files for this tenant</div>
                </div>
                <button
                  onClick={handleDeprovision}
                  className="btn btn-ghost btn-sm"
                  style={{ color: 'var(--red)', borderColor: 'rgba(220,38,38,0.3)', flexShrink: 0 }}
                >
                  Deprovision SFTP
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <p style={{ color: 'var(--text-mid)', fontSize: 14, marginBottom: 16 }}>
              Provisioning creates an AWS Transfer Family SFTP server, S3 bucket, and SSH key pair. AP21 connects using the SSH private key.
            </p>
            <button onClick={handleProvision} disabled={provisioning} className="btn btn-primary">
              {provisioning ? <><Loader size={15} className="spin" /> Provisioning…</> : <><Server size={15} /> Provision SFTP</>}
            </button>
          </>
        )}
      </div>

      {/* SSH Key — only when a stored key pair exists (custom-IdP tenants have none) */}
      {config?.provisioned && config?.public_key && (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Key size={16} style={{ color: 'var(--text-dim)' }} />
              <span className="card-title" style={{ margin: 0 }}>SSH Private Key</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleShowKey} className="btn btn-ghost btn-sm">
                {showKey ? <><ChevronUp size={13} /> Hide</> : <><ChevronDown size={13} /> Show Key</>}
              </button>
              <button onClick={handleRotate} disabled={rotating} className="btn btn-ghost btn-sm" style={{ color: 'var(--amber)' }}>
                {rotating ? <Loader size={13} className="spin" /> : <RefreshCw size={13} />} Rotate Key
              </button>
            </div>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-mid)', marginBottom: showKey ? 12 : 0 }}>
            Provide this private key to AP21 to authenticate their SFTP connection.
          </p>
          {showKey && privateKey && (
            <div style={{ position: 'relative', marginBottom: 12 }}>
              <pre style={{ background: 'var(--navy)', border: '1px solid var(--navy-border)', borderRadius: 8, padding: 16, fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--green)', overflow: 'auto', maxHeight: 220, margin: 0 }}>{privateKey}</pre>
              <button onClick={() => copy(privateKey, 'Private key')} className="btn btn-ghost btn-sm" style={{ position: 'absolute', top: 8, right: 8 }}>
                <Copy size={12} /> Copy
              </button>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, padding: '10px 14px', background: 'rgba(245,158,11,0.08)', borderRadius: 8, border: '1px solid rgba(245,158,11,0.2)' }}>
            <Shield size={14} style={{ color: 'var(--amber)', marginTop: 2, flexShrink: 0 }} />
            <p style={{ fontSize: 12, color: 'var(--amber)', margin: 0, lineHeight: 1.5 }}>
              <strong>Rotating the key immediately invalidates the old key.</strong> AP21 cannot connect until you provide them the new private key.
            </p>
          </div>
        </div>
      )}

      {/* Schedule & Alerts */}
      {config?.provisioned && (
        <div className="card">
          <div className="card-title">Schedule &amp; Alerts</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Automatic sync</div>
                <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>Translate files automatically when AP21 uploads them</div>
              </div>
              <button onClick={() => setForm(f => ({ ...f, schedule_enabled: !f.schedule_enabled }))}
                style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', background: form.schedule_enabled ? 'var(--blue)' : 'var(--navy-border)', position: 'relative', transition: 'background 0.2s' }}>
                <span style={{ position: 'absolute', top: 2, left: form.schedule_enabled ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
              </button>
            </div>
            <div>
              <label className="input-label">Check interval</label>
              <select value={form.poll_interval} onChange={e => setForm(f => ({ ...f, poll_interval: parseInt(e.target.value) }))} className="input">
                {INTERVALS.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
              </select>
            </div>
            <div>
              <label className="input-label">Alert email</label>
              <input type="email" value={form.alert_email} onChange={e => setForm(f => ({ ...f, alert_email: e.target.value }))} placeholder="alerts@company.com" className="input" />
            </div>
            <div>
              <label className="input-label">Slack webhook URL <span style={{ color: 'var(--text-dim)' }}>(optional)</span></label>
              <input type="text" value={form.alert_slack_webhook} onChange={e => setForm(f => ({ ...f, alert_slack_webhook: e.target.value }))} placeholder="https://hooks.slack.com/..." className="input" />
            </div>
            <button onClick={handleSave} disabled={saving} className="btn btn-primary" style={{ alignSelf: 'flex-start' }}>
              {saving && <Loader size={15} className="spin" />} Save Settings
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── FTP Panel ─────────────────────────────────────────────────────────────────
function FTPPanel() {
  const [form, setForm] = useState({
    host: '', port: 21, username: '', password: '',
    source_dir: '/incoming', output_dir: '/outgoing', archive_dir: '',
    poll_interval: 300, schedule_enabled: true,
    alert_email: '', alert_slack_webhook: '',
  });
  const [saving, setSaving]       = useState(false);
  const [testing, setTesting]     = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [isNew, setIsNew]         = useState(true);

  useEffect(() => {
    ftpApi.getConfig()
      .then(({ data }) => { setForm({ ...data, password: '' }); setIsNew(false); })
      .catch(() => setIsNew(true));
  }, []);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggle = k => setForm(f => ({ ...f, [k]: !f[k] }));

  const handleSave = async e => {
    e.preventDefault(); setSaving(true);
    try { await ftpApi.saveConfig(form); setIsNew(false); toast.success('FTP settings saved'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try { const { data } = await ftpApi.testConnection(form); setTestResult(data); }
    catch (err) { setTestResult({ success: false, message: err.response?.data?.detail || 'Connection failed' }); }
    finally { setTesting(false); }
  };

  return (
    <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="card">
        <div className="card-title">FTP Connection</div>
        <div className="form-grid">
          <div className="form-group">
            <label>Host</label>
            <input value={form.host} onChange={set('host')} placeholder="ftp.yourserver.com" required />
          </div>
          <div className="form-group">
            <label>Port</label>
            <input type="number" value={form.port} onChange={set('port')} min={1} max={65535} />
          </div>
          <div className="form-group">
            <label>Username</label>
            <input value={form.username} onChange={set('username')} required />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input type="password" value={form.password} onChange={set('password')} placeholder={isNew ? 'Enter password' : '••••••••  (leave blank to keep current)'} />
          </div>
        </div>
        {testResult && (
          <div className={`alert-box ${testResult.success ? 'alert-success' : 'alert-error'}`} style={{ marginTop: 14 }}>
            {testResult.success ? <CheckCircle size={16} /> : <XCircle size={16} />}
            <span>{testResult.message}</span>
          </div>
        )}
        <button type="button" className={`btn btn-ghost btn-sm ${testing ? 'btn-loading' : ''}`} onClick={handleTest} disabled={testing || !form.host} style={{ marginTop: 14 }}>
          <TestTube size={14} /> {testing ? 'Testing…' : 'Test Connection'}
        </button>
      </div>

      <div className="card">
        <div className="card-title">FTP Directories</div>
        <div className="form-grid">
          <div className="form-group">
            <label>Source Directory</label>
            <input value={form.source_dir} onChange={set('source_dir')} placeholder="/ap21/exports" />
            <span className="form-hint">Where AP21 drops export files</span>
          </div>
          <div className="form-group">
            <label>Output Directory</label>
            <input value={form.output_dir} onChange={set('output_dir')} placeholder="/ezy/imports" />
            <span className="form-hint">Where translated files are uploaded</span>
          </div>
          <div className="form-group full">
            <label>Archive Directory <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--text-dim)' }}>(optional)</span></label>
            <input value={form.archive_dir} onChange={set('archive_dir')} placeholder="/ap21/processed" />
            <span className="form-hint">Leave blank to disable archiving of source files</span>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">Sync Schedule</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="toggle-wrap">
            <div className={`toggle ${form.schedule_enabled ? 'on' : ''}`} onClick={() => toggle('schedule_enabled')} />
            <span className="toggle-label">Automatic scheduling enabled</span>
          </div>
          {form.schedule_enabled && (
            <div className="form-group" style={{ maxWidth: 280 }}>
              <label>Check interval</label>
              <select value={form.poll_interval} onChange={set('poll_interval')}>
                {INTERVALS.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-title">Failure Alerts</div>
        <div className="form-grid">
          <div className="form-group">
            <label>Alert Email</label>
            <input type="email" value={form.alert_email} onChange={set('alert_email')} placeholder="ops@yourcompany.com" />
            <span className="form-hint">Receive an email when a sync fails</span>
          </div>
          <div className="form-group">
            <label>Slack Webhook <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--text-dim)' }}>(optional)</span></label>
            <input value={form.alert_slack_webhook} onChange={set('alert_slack_webhook')} placeholder="https://hooks.slack.com/..." />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="submit" className={`btn btn-primary ${saving ? 'btn-loading' : ''}`} disabled={saving}>
          <Save size={15} /> {saving ? 'Saving…' : 'Save Settings'}
        </button>
      </div>
    </form>
  );
}


// ── File Mappings Panel ──────────────────────────────────────────────────────
const FILE_TYPE_LABELS = {
  customer:    { label: 'Customer',    outgoing: 'Customer.csv',    color: 'var(--blue)'  },
  contact:     { label: 'Contact',     outgoing: 'Contact.csv',     color: 'var(--teal)'  },
  transaction: { label: 'Transaction', outgoing: 'Transaction.csv', color: 'var(--amber)' },
  item:        { label: 'Item',        outgoing: 'Item.csv',        color: 'var(--green)' },
};

const MATCH_MODE_DOCS = {
  startswith: { label: 'Starts with', hint: 'Filename must start with this text',         example: 'ezy_customer_  →  ezy_customer_20260522.csv' },
  contains:   { label: 'Contains',    hint: 'Pattern appears anywhere in the filename',   example: '_customer_     →  ap21_customer_export.csv'  },
  endswith:   { label: 'Ends with',   hint: 'Filename must end with this text',           example: '_customers.csv →  daily_customers.csv'       },
  regex:      { label: 'Regex',       hint: 'Full Python-compatible regular expression',  example: 'cust.*\\.csv   →  customers.csv, cust_2026.csv' },
};

function FileMappingsPanel({ readOnly = false }) {
  const [mappings, setMappings]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [editing, setEditing]         = useState(null);
  const [editForm, setEditForm]       = useState({});
  const [saving, setSaving]           = useState(false);
  const [testFile, setTestFile]       = useState('');
  const [testResults, setTestResults] = useState({});

  useEffect(() => { loadMappings(); }, []);

  const loadMappings = async () => {
    setLoading(true);
    try { const { data } = await mappingsApi.list(); setMappings(data); }
    catch { toast.error('Failed to load file mappings'); }
    finally { setLoading(false); }
  };

  const startEdit = (m) => {
    setEditing(m.file_type);
    setEditForm({ pattern: m.pattern, match_mode: m.match_mode, enabled: m.enabled });
    setTestFile('');
    setTestResults({});
  };

  const handleSave = async (fileType) => {
    setSaving(true);
    try {
      await mappingsApi.update(fileType, editForm);
      toast.success('Mapping saved');
      setEditing(null);
      await loadMappings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleTest = async (fileType) => {
    if (!testFile.trim()) return;
    try {
      const { data } = await mappingsApi.testPattern({
        pattern: editForm.pattern,
        match_mode: editForm.match_mode,
        filename: testFile.trim(),
      });
      setTestResults(r => ({ ...r, [fileType]: data }));
    } catch (e) {
      setTestResults(r => ({ ...r, [fileType]: { matches: false, error: e.response?.data?.detail } }));
    }
  };

  const toggleEnabled = async (m) => {
    try {
      await mappingsApi.update(m.file_type, { pattern: m.pattern, match_mode: m.match_mode, enabled: !m.enabled });
      await loadMappings();
    } catch { toast.error('Failed to update'); }
  };

  if (loading) return <div style={{ padding: 24, textAlign: 'center' }}><Loader size={20} className="spin" style={{ color: 'var(--text-dim)' }} /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {readOnly && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                      borderRadius: 8, background: 'rgba(37,99,235,0.06)',
                      border: '1px solid rgba(37,99,235,0.2)', fontSize: 13, color: 'var(--blue)' }}>
          <Info size={14} style={{ flexShrink: 0 }} />
          <span>File mappings are managed by your platform administrator. Contact them to make changes.</span>
        </div>
      )}
      <div style={{ padding: '12px 16px', background: 'rgba(37,99,235,0.08)', borderRadius: 8, border: '1px solid rgba(37,99,235,0.2)', fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.6 }}>
        <strong style={{ color: 'var(--blue)' }}>How this works — </strong>
        Each incoming filename is tested against the patterns below. The first match determines which translator runs.
        Output filenames are fixed by EzyCollect and cannot be changed.
      </div>

      {mappings.map(m => {
        const meta    = FILE_TYPE_LABELS[m.file_type] || {};
        const modeDoc = MATCH_MODE_DOCS[m.match_mode]  || {};
        const isEdit  = editing === m.file_type;
        const result  = testResults[m.file_type];

        return (
          <div key={m.file_type} className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: isEdit ? '1px solid var(--navy-border)' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button onClick={() => !isEdit && !readOnly && toggleEnabled(m)} style={{ width: 36, height: 20, borderRadius: 10, border: 'none', cursor: (isEdit || readOnly) ? 'default' : 'pointer', background: m.enabled ? 'var(--green)' : 'var(--navy-border)', position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
                  <span style={{ position: 'absolute', top: 2, left: m.enabled ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.15s' }} />
                </button>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{meta.label}</span>
                    <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 4, background: 'var(--navy)', color: meta.color, border: `1px solid ${meta.color}25`, fontFamily: 'var(--font-mono)' }}>
                      {'\u2192'} {m.outgoing_name}
                    </span>
                  </div>
                  {!isEdit && (
                    <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 3 }}>
                      <span style={{ color: 'var(--text-mid)', fontFamily: 'var(--font-mono)' }}>{m.pattern}</span>
                      <span style={{ margin: '0 6px' }}>·</span>
                      <span>{modeDoc.label}</span>
                    </div>
                  )}
                </div>
              </div>
              {!readOnly && (!isEdit
                ? <button onClick={() => startEdit(m)} className="btn btn-ghost btn-sm">Edit</button>
                : <button onClick={() => setEditing(null)} className="btn btn-ghost btn-sm"><X size={13} /> Cancel</button>
              )}
            </div>

            {/* Edit form */}
            {isEdit && (
              <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label className="input-label">Filename pattern</label>
                    <input className="input" value={editForm.pattern} onChange={e => setEditForm(f => ({ ...f, pattern: e.target.value }))} placeholder="e.g. ezy_customer_" />
                  </div>
                  <div style={{ width: 160 }}>
                    <label className="input-label">Match mode</label>
                    <select className="input" value={editForm.match_mode} onChange={e => setEditForm(f => ({ ...f, match_mode: e.target.value }))}>
                      {Object.entries(MATCH_MODE_DOCS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </div>
                </div>

                <div style={{ fontSize: 12, color: 'var(--text-dim)', background: 'var(--navy)', padding: '10px 14px', borderRadius: 8, lineHeight: 1.7 }}>
                  <strong style={{ color: 'var(--text-mid)' }}>{MATCH_MODE_DOCS[editForm.match_mode]?.hint}</strong><br />
                  <span style={{ fontFamily: 'var(--font-mono)' }}>Example: {MATCH_MODE_DOCS[editForm.match_mode]?.example}</span>
                </div>

                <div>
                  <label className="input-label">Test a filename</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input className="input" value={testFile} onChange={e => { setTestFile(e.target.value); setTestResults(r => ({ ...r, [m.file_type]: null })); }} placeholder={`e.g. ${m.pattern}20260522.csv`} style={{ flex: 1 }} />
                    <button type="button" onClick={() => handleTest(m.file_type)} className="btn btn-ghost btn-sm" disabled={!testFile.trim()}>Test</button>
                  </div>
                  {result && (
                    <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 6, fontSize: 13, background: result.matches ? 'rgba(34,197,94,0.08)' : 'rgba(220,38,38,0.08)', border: `1px solid ${result.matches ? 'rgba(34,197,94,0.25)' : 'rgba(220,38,38,0.25)'}`, color: result.matches ? 'var(--green)' : 'var(--red)' }}>
                      {result.error ? `\u26a0\ufe0f ${result.error}` : result.matches ? `\u2705 "${testFile}" matches \u2014 will produce ${m.outgoing_name}` : `\u274c "${testFile}" does not match this pattern`}
                    </div>
                  )}
                </div>

                <button onClick={() => handleSave(m.file_type)} disabled={saving || !editForm.pattern.trim()} className="btn btn-primary" style={{ alignSelf: 'flex-start' }}>
                  {saving ? <Loader size={14} className="spin" /> : null} Save Mapping
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Combined page ─────────────────────────────────────────────────────────────
export default function ConnectionSettings() {
  const { user, activeTenantId } = useAuth();
  const isAdmin = user?.is_admin;
  if (isAdmin && !activeTenantId) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', textAlign: 'center' }}>
      <Server size={40} style={{ color: 'var(--text-dim)', marginBottom: 16 }} />
      <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>No Tenant Selected</h3>
      <p style={{ fontSize: 13, color: 'var(--text-dim)', maxWidth: 320, lineHeight: 1.6 }}>Use the tenant switcher in the sidebar to select a tenant and manage their connection settings.</p>
    </div>
  );
  const [activeTab, setActiveTab]     = useState(null); // null = loading
  const [sftpProvisioned, setSftp]    = useState(false);
  const [ftpConfigured, setFtp]       = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      sftpApi.getConfig(),
      ftpApi.getConfig(),
    ]).then(([sftpRes, ftpRes]) => {
      const sftp = sftpRes.status === 'fulfilled' && sftpRes.value.data.provisioned;
      const ftp  = ftpRes.status  === 'fulfilled';
      setSftp(sftp);
      setFtp(ftp);
      // Default to whichever is active; if both or neither, default to SFTP
      setActiveTab(sftp ? 'sftp' : 'ftp');
    }).finally(() => setLoadingStatus(false));
  }, []);

  const Tab = ({ id, label, icon: Icon, active }) => {
    const isActive  = activeTab === id;
    const hasActive = id === 'sftp' ? sftpProvisioned : ftpConfigured;
    return (
      <button
        onClick={() => setActiveTab(id)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 20px',
          background: isActive ? 'var(--surface)' : 'transparent',
          border: 'none', borderBottom: isActive ? '2px solid var(--blue)' : '2px solid transparent',
          cursor: 'pointer', color: isActive ? 'var(--text)' : 'var(--text-dim)',
          fontWeight: isActive ? 600 : 400, fontSize: 14, fontFamily: 'inherit',
          transition: 'all 0.15s', whiteSpace: 'nowrap',
        }}
      >
        <Icon size={15} />
        {label}
        {hasActive && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 6px',
            borderRadius: 10, background: 'rgba(34,197,94,0.15)',
            color: 'var(--green)', border: '1px solid rgba(34,197,94,0.25)',
            textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            Active
          </span>
        )}
      </button>
    );
  };

  if (loadingStatus) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1 className="page-title">Connection Settings</h1>
        <p className="page-subtitle">Configure how AP21 delivers files to the sync platform.</p>
      </div>

      {/* Tab bar */}
      <div style={{
        display: 'flex', borderBottom: '1px solid var(--navy-border)',
        marginBottom: 24, background: 'var(--navy)', borderRadius: '10px 10px 0 0',
        padding: '0 4px',
      }}>
        <Tab id="sftp"     label="Hosted SFTP"       icon={Server}   />
        <Tab id="ftp"      label="FTP"            icon={Wifi}     />
        <Tab id="mappings" label="File Mappings" icon={Settings2} />
      </div>

      {activeTab === 'sftp'     && <SFTPPanel />}
      {activeTab === 'ftp'      && <FTPPanel />}
      {activeTab === 'mappings' && <FileMappingsPanel readOnly={!isAdmin} />}
    </div>
  );
}
